// apps/client/src/characters/npcs.ts (M1-T14; characters pass 2026-10-16) — Bramble, an original badger shopkeeper (D-20), built on Kenney's
// CC0 rig with the doll part toolkit (`body`, the head and face helpers, §8.5), never through `DollSpec`: a chubby grey body in a leaf-green
// apron, dark legs and paws, and a badger head whose white stripe runs from the snout over the crown between two dark eye bands, with white
// cheeks, a long white snout and small rimmed ears. Baked into one skinned mesh like every doll (one draw call per pass).
import * as THREE from 'three';
import type { RigSource } from './avatar';
import { bakeDoll, bakedToyMaterial, body, disposeDoll, hideRigMeshes, RIG_HEIGHT } from './doll';
import { face, HEAD, headShape, onHead, smile, stick } from './head';
import { part } from './part';
import { blob, lathe } from './shapes';

// Presentation colours (DESIGN.md palette: the apron is leaf-700, the pocket cream-300).
const FUR = '#8E8985', WHITE = '#F4F1EA', DARK = '#3A3634', APRON = '#3A7D44', POCKET = '#EAD6AE', NOSE = '#2A2422';
const HEIGHT = 1.45;                     // metres: a touch shorter than residents (tuning.move.residentHeight)
const GIRTH = 1.16;

/** A patch of the head's surface a hair proud of it, with its poles on the X axis, so a `theta` range is a band running front → crown →
 *  back at a fixed distance from the middle, and `phi` (0 = top, π/2 = front, π = chin) says how far round the head it goes. */
function headBand(theta0: number, theta1: number, phi0: number, phi1: number, w: number, h: number): THREE.BufferGeometry {
  return new THREE.SphereGeometry(1, w, h, phi0, phi1 - phi0, theta0, theta1 - theta0).rotateZ(-Math.PI / 2)
    .scale(HEAD.r.x * 1.014, HEAD.r.y * 1.014, HEAD.r.z * 1.014).translate(HEAD.c.x, HEAD.c.y, HEAD.c.z);
}

/** Dresses `rig` as Bramble (parts named for tests: badgerStripe, badgerEarL/R, badgerApron, badgerSnout, eye-left/right). */
export function dressShopkeeper(rig: THREE.Object3D): void {
  hideRigMeshes(rig);
  const { head, torso } = body(rig, { shirt: FUR, pants: DARK, shoes: DARK, hands: DARK, collar: APRON, girth: GIRTH });
  // The apron over the front of the barrel (its strap is the collar) with a cream pocket, and a stub of a tail.
  const apron = part(torso, lathe([[0.104, -0.012], [0.121, -0.002], [0.125, 0.026], [0.121, 0.06], [0.108, 0.095], [0.09, 0.124]], 20, -1.15, 2.3), APRON, [0, 0, 0], 'badgerApron');
  apron.scale.set(GIRTH, 1, GIRTH * 0.93);
  part(torso, blob(0.045, 0.034, 0.014, 10, 6), POCKET, [0, 0.03, 0.129], 'badgerPocket');
  part(torso, blob(0.04, 0.034, 0.048, 8, 6), FUR, [0, 0.03, -0.118], 'badgerTail');

  // Head: dark base; the white stripe and cheeks leave the two eye bands dark.
  headShape(head, DARK);
  part(head, headBand(Math.PI / 2 - 0.2, Math.PI / 2 + 0.2, -1.9, 2.05, 20, 3), WHITE, [0, 0, 0], 'badgerStripe');
  part(head, headBand(0, Math.PI / 2 - 0.66, -2.2, 2.6, 14, 6), WHITE, [0, 0, 0], 'badgerCheekL');          // theta from +X: the left side
  part(head, headBand(Math.PI / 2 + 0.66, Math.PI, -2.2, 2.6, 14, 6), WHITE, [0, 0, 0], 'badgerCheekR');
  face(head, { skin: DARK, eyes: 'round', mouthLat: null });
  stick(head, blob(0.056, 0.048, 0.085, 12, 8), WHITE, -0.12, 0, -0.03, 'badgerSnout');
  stick(head, blob(0.026, 0.02, 0.02, 10, 6), NOSE, -0.02, 0, 0.052, 'badgerNose');
  smile(head, -0.27, 0, { name: 'badgerSmile', lift: 0.04 });
  // Small round ears with white rims, low on the sides of the crown (the character faces +Z, so its left is +X).
  for (const [sx, name] of [[1, 'badgerEarL'], [-1, 'badgerEarR']] as const) {
    const { p, n } = onHead(0.78, sx * 1.15, 0.006);
    const ear = part(head, blob(0.044, 0.04, 0.022, 10, 6), DARK, [p.x, p.y, p.z], name);
    ear.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), n.clone().lerp(new THREE.Vector3(0, 0, 1), 0.6).normalize());
    part(ear, new THREE.TorusGeometry(0.036, 0.009, 4, 12), WHITE, [0, 0, 0.008], `${name}Rim`);
  }
}

export function createShopkeeper(rig: RigSource): { root: THREE.Group; update(dt: number): void; dispose(): void } {
  const root = new THREE.Group(); root.name = 'shopkeeper';
  const figure = rig.scene;
  dressShopkeeper(figure);
  bakeDoll(figure, bakedToyMaterial());            // one draw call per pass instead of ~40; unpatched, like the toy materials it replaces
  root.add(figure);
  root.scale.setScalar(HEIGHT / RIG_HEIGHT);
  const mixer = new THREE.AnimationMixer(figure);
  const idle = rig.clips.find((c) => c.name === 'idle');
  if (idle) mixer.clipAction(idle).play();
  return { root, update: (dt) => mixer.update(dt), dispose: () => { mixer.stopAllAction(); mixer.uncacheRoot(figure); disposeDoll(root); } };
}
